<template>
  <ReturnToOwnerModal
    v-model="escrowReturnOpen"
    :rows="store.escrowAttributions"
    :commitmentId="escrowCommitmentId"
    :freeAttributionIds
    :title="escrowModalTitle"
    :contributions="escrowContributions"
    :canReturn="escrowCanReturn"
    :commitmentGone="escrowCommitmentGone"
    :loading="store.escrowAttributionsLoading"
    :busy="store.busyAction === 'escrow_return'"
    :errorMessage="store.errorOn('escrow-return')"
    :results="escrowReturnDialog?.results ?? null"
    @submit="onReturnEscrow"
  />

  <PullToEscrowModal
    v-model="escrowPullOpen"
    :commitmentId="pullCommitmentId"
    :members="pullMembers"
    :escrowedMwh="pullEscrowedMwh"
    :committedMwh="pullCommittedMwh"
    :commitmentGone="pullCommitmentGone"
    :busy="store.busyAction === 'escrow_pull'"
    :errorMessage="store.errorOn('escrow-pull')"
    :results="escrowPullDialog?.results ?? null"
    @submit="onPullEscrow"
  />
</template>

<script setup lang="ts">
import { formatDateTimeIntoDate } from "@/composables/formatUtils";
import PullToEscrowModal from "@/forwards2/deals/PullToEscrowModal.vue";
import ReturnToOwnerModal from "@/forwards2/deals/ReturnToOwnerModal.vue";
import { useActiveDialog } from "@/forwards2/deals/useActiveDialog";
import { useDealStore } from "@/forwards2/deals/useDealStore";
import type {
  EscrowMoveItemResult,
  EscrowPullRequest,
} from "@/forwards2/forwardsTypes";
import { sumBy } from "lodash-es";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

type EscrowDialog =
  | {
      kind: "escrow-return";
      commitmentId: number | null;
      results: EscrowMoveItemResult[] | null;
    }
  | {
      kind: "escrow-pull";
      commitmentId: number;
      results: EscrowMoveItemResult[] | null;
    };

/**
 * The deal page's escrow dialogs, which the page opens through `openReturn` and `openPull`: returning
 * escrow to its owners, from a commitment or from the free-escrow card, and pulling escrow into a
 * commitment. A move answers with per-item results, which the dialog that sent it shows.
 */
const store = useDealStore();
const { t } = useI18n();
const {
  open: openDialog,
  update: updateDialog,
  entry: dialogEntry,
  model: dialogModel,
} = useActiveDialog<EscrowDialog>();

const escrowReturnOpen = dialogModel("escrow-return");
const escrowPullOpen = dialogModel("escrow-pull");

const escrowReturnDialog = dialogEntry("escrow-return");
const escrowPullDialog = dialogEntry("escrow-pull");

const freeAttributionIds = computed(() =>
  store.freeEscrow.map((row) => row.attribution_id)
);

const escrowCommitmentId = computed(
  () => escrowReturnDialog.value?.commitmentId ?? null
);
const escrowCommitment = computed(() =>
  store.commitmentById(escrowCommitmentId.value)
);

/**
 * Whether a refresh took the commitment a dialog was opened for off the deal — its backing deleted,
 * the deal cancelled. The dialog says so and refuses its move, rather than falling back to the
 * free-escrow form it would otherwise look like. A failed load leaves no deal at all, which says
 * nothing about the commitment.
 */
const isCommitmentGone = (commitmentId: number | null) =>
  commitmentId !== null &&
  store.deal !== null &&
  store.commitmentById(commitmentId) === null;

const escrowCommitmentGone = computed(() =>
  isCommitmentGone(escrowCommitmentId.value)
);

/**
 * The escrow dialog: a commitment's per-company breakdown for everyone, the return form for a super
 * admin while the commitment is live. From the free-escrow card there is no commitment, only the
 * form. Read from the deal rather than copied on open, so a return's refresh reaches the open dialog.
 */
const escrowContributions = computed(
  () => escrowCommitment.value?.escrow_by_company ?? null
);
const escrowModalTitle = computed(() => {
  const commitment = escrowCommitment.value;
  const group = store.commitmentGroupOf(escrowCommitmentId.value);
  return commitment && group
    ? t("forwards.deals.commitments.escrow_title", {
        mandate: group.mandate_name,
        date: formatDateTimeIntoDate(commitment.created_at),
      })
    : null;
});
const escrowCanReturn = computed(
  () =>
    store.capabilities?.can_manage === true &&
    (escrowCommitment.value?.released_at ?? null) === null
);

const pullCommitmentId = computed(
  () => escrowPullDialog.value?.commitmentId ?? null
);
const pullCommitment = computed(() =>
  store.commitmentById(pullCommitmentId.value)
);
const pullCommitmentGone = computed(() =>
  isCommitmentGone(pullCommitmentId.value)
);
/** The owners the pull may draw from, carried on the deal under the viewer's own scope (C-09b-07). */
const pullMembers = computed(
  () => store.commitmentGroupOf(pullCommitmentId.value)?.members ?? []
);
const pullEscrowedMwh = computed(() =>
  sumBy(pullCommitment.value?.slices ?? [], "escrowed_mwh")
);
const pullCommittedMwh = computed(() =>
  sumBy(pullCommitment.value?.slices ?? [], "volume_mwh")
);

/** The attribution list is an admin read, so it is fetched only when the return form will show. */
const openReturn = (commitmentId: number | null) => {
  store.clearActionError();
  openDialog({ kind: "escrow-return", commitmentId, results: null });
  if (escrowCanReturn.value) void store.loadEscrowAttributions();
};

const onReturnEscrow = async (
  selection: { attribution_id: number; count: number }[]
) => {
  if (escrowCommitmentGone.value) return;
  const opened = escrowReturnDialog.value;
  const result = await store.returnEscrow({ selection });
  if (result !== null && opened !== null)
    updateDialog(opened, { results: result.items });
};

const openPull = (commitmentId: number) => {
  store.clearActionError();
  openDialog({ kind: "escrow-pull", commitmentId, results: null });
};

const onPullEscrow = async (body: EscrowPullRequest) => {
  if (pullCommitmentGone.value) return;
  const opened = escrowPullDialog.value;
  const result = await store.pullEscrow(body);
  if (result !== null && opened !== null)
    updateDialog(opened, { results: result.items });
};

defineExpose({ openReturn, openPull });
</script>
